import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    AcquiredDate,
    PurchasableGift,
    GiftToSend,
    ReceivedGift,
} from "JS/Models";
import { ContentDetailsMap } from "./Audio";

export interface GiftState {
    audioGiftsList: any[];
    videoGiftsList: any[];
    purchasableMemberAudios: PurchasableGift[];
    purchasableMemberVideos: PurchasableGift[];
    purchasableProspectAudios: PurchasableGift[];
    purchasableProspectVideos: PurchasableGift[];
    sentAudioGiftsList: any[];
    sentVideoGiftsList: any[];
    audiosToSend: GiftToSend[];
    videosToSend: GiftToSend[];
    receivedMemberAudioGifts: any[];
    receivedMemberVideoGifts: any[];
    receivedProspectAudioGifts: any[];
    receivedMemberAudios: ReceivedGift[];
    receivedMemberVideos: ReceivedGift[];
    receivedProspectAudios: ReceivedGift[];
    receivedProspectVideos: ReceivedGift[];
    receivedProspectVideoGifts: any;
    audioGiftsGiven: any[];
    videoGiftsGiven: any[];
    acquiredDates: AcquiredDate;
    videoAcquiredDates: AcquiredDate;
    audioAcquiredDates: AcquiredDate;
    courseAcquiredDates: AcquiredDate;
    prosAudioAcquiredDates: AcquiredDate;
    prosVideoAcquiredDates: AcquiredDate;
    giftDetails: ContentDetailsMap;
}

const initialState: GiftState = {
    audioGiftsList: [],
    videoGiftsList: [],
    purchasableMemberAudios: [],
    purchasableMemberVideos: [],
    purchasableProspectAudios: [],
    purchasableProspectVideos: [],
    sentAudioGiftsList: [],
    sentVideoGiftsList: [],
    audiosToSend: [],
    videosToSend: [],
    receivedMemberAudioGifts: [],
    receivedMemberVideoGifts: [],
    receivedProspectAudioGifts: [],
    receivedProspectVideoGifts: [],
    audioGiftsGiven: [],
    videoGiftsGiven: [],
    acquiredDates: {},
    audioAcquiredDates: {},
    videoAcquiredDates: {},
    courseAcquiredDates: {},
    prosAudioAcquiredDates: {},
    prosVideoAcquiredDates: {},
    receivedMemberAudios: [],
    receivedProspectAudios: [],
    receivedProspectVideos: [],
    receivedMemberVideos: [],
    giftDetails: {},
};

export const giftStateSlice = createSlice({
    name: "giftState",
    initialState,
    reducers: {
        setGlobalGifts: (state, data) => {
            if (data.payload.sentAudioGiftsList)
                state.sentAudioGiftsList = data.payload.sentAudioGiftsList;
            if (data.payload.sentVideoGiftsList)
                state.sentVideoGiftsList = data.payload.sentVideoGiftsList;
            if (data.payload.receivedMemberAudioGifts)
                state.receivedMemberAudioGifts =
                    data.payload.receivedMemberAudioGifts;
            if (data.payload.receivedMemberVideoGifts)
                state.receivedMemberVideoGifts =
                    data.payload.receivedMemberVideoGifts;
            if (data.payload.receivedProspectAudioGifts)
                state.receivedProspectAudioGifts =
                    data.payload.receivedProspectAudioGifts;
            if (data.payload.receivedProspectVideoGifts)
                state.receivedProspectVideoGifts =
                    data.payload.receivedProspectVideoGifts;
            if (data.payload.audioGiftsGiven)
                state.audioGiftsGiven = data.payload.audioGiftsGiven;
            if (data.payload.videoGiftsGiven)
                state.videoGiftsGiven = data.payload.videoGiftsGiven;
            if (data.payload.acquiredDates)
                state.acquiredDates = {
                    ...data.payload.acquiredDates,
                    ...state.acquiredDates,
                };
            if (data.payload.audioAcquiredDates)
                state.audioAcquiredDates = {
                    ...state.audioAcquiredDates,
                    ...data.payload.audioAcquiredDates,
                };
            if (data.payload.videoAcquiredDates)
                state.videoAcquiredDates = {
                    ...state.videoAcquiredDates,
                    ...data.payload.videoAcquiredDates,
                };
            if (data.payload.courseAcquiredDates)
                state.courseAcquiredDates = {
                    ...state.courseAcquiredDates,
                    ...data.payload.courseAcquiredDates,
                };
            if (data.payload.prosAudioAcquiredDates)
                state.prosAudioAcquiredDates = {
                    ...state.prosAudioAcquiredDates,
                    ...data.payload.prosAudioAcquiredDates,
                };
            if (data.payload.prosVideoAcquiredDates)
                state.prosVideoAcquiredDates = {
                    ...state.prosVideoAcquiredDates,
                    ...data.payload.prosVideoAcquiredDates,
                };
            return state;
        },
        setPurchasableMemberAudios: (
            state,
            data: PayloadAction<GiftState>,
        ) => ({
            ...state,
            purchasableMemberAudios: data.payload.purchasableMemberAudios,
            audioGiftsList: [],
        }),
        setPurchasableMemberVideos: (
            state,
            data: PayloadAction<GiftState>,
        ) => ({
            ...state,
            purchasableMemberVideos: data.payload.purchasableMemberVideos,
            videoGiftsList: [],
        }),
        setPurchasableProspectAudios: (
            state,
            data: PayloadAction<GiftState>,
        ) => ({
            ...state,
            purchasableProspectAudios: data.payload.purchasableProspectAudios,
            audioGiftsList: [],
        }),
        setPurchasableProspectVideos: (
            state,
            data: PayloadAction<GiftState>,
        ) => ({
            ...state,
            purchasableProspectVideos: data.payload.purchasableProspectVideos,
            videoGiftsList: [],
        }),
        setAudiosToSend: (state, data: PayloadAction<GiftState>) => ({
            ...state,
            audiosToSend: data.payload.audiosToSend,
            sentAudioGiftsList: [],
        }),
        setVideosToSend: (state, data: PayloadAction<GiftState>) => ({
            ...state,
            videosToSend: data.payload.videosToSend,
            sentVideoGiftsList: [],
        }),
        setReceivedMemberAudios: (state, data: PayloadAction<GiftState>) => ({
            ...state,
            receivedMemberAudios: data.payload.receivedMemberAudios,
            receivedMemberAudioGifts: [],
        }),
        setReceivedMemberVideos: (state, data: PayloadAction<GiftState>) => ({
            ...state,
            receivedMemberVideos: data.payload.receivedMemberVideos,
            receivedMemberVideoGifts: [],
        }),
        setReceivedProspectAudios: (state, data: PayloadAction<GiftState>) => ({
            ...state,
            receivedProspectAudios: data.payload.receivedProspectAudios,
            receivedProspectAudioGifts: [],
        }),
        setReceivedProspectVideos: (state, data: PayloadAction<GiftState>) => ({
            ...state,
            receivedProspectVideos: data.payload.receivedProspectVideos,
            receivedProspectVideoGifts: [],
        }),
        softResetGiftState: (state) => {
            return {
                ...initialState,
                receivedMemberAudioGifts: state.receivedMemberAudioGifts,
                receivedMemberVideoGifts: state.receivedMemberVideoGifts,
                receivedProspectAudioGifts: state.receivedProspectAudioGifts,
                receivedProspectVideoGifts: state.receivedProspectVideoGifts,
            };
        },
        setGiftDetails: (state, data: PayloadAction<ContentDetailsMap>) => {
            return { ...state, giftDetails: data.payload };
        },
        resetGiftState: () => {
            return initialState;
        },
    },
});
export const {
    setGlobalGifts,
    resetGiftState,
    softResetGiftState,
    setPurchasableMemberAudios,
    setPurchasableMemberVideos,
    setPurchasableProspectAudios,
    setPurchasableProspectVideos,
    setAudiosToSend,
    setVideosToSend,
    setReceivedMemberAudios,
    setReceivedMemberVideos,
    setReceivedProspectAudios,
    setReceivedProspectVideos,
    setGiftDetails,
} = giftStateSlice.actions;
